import { useState, useEffect } from 'react';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Clock, Save, RotateCcw } from 'lucide-react';

const HOURS = Array.from({ length: 24 }, (_, i) => i);

const DAYS_OF_WEEK = [
  { value: 0, label: 'Dimanche', shortLabel: 'Dim' },
  { value: 1, label: 'Lundi', shortLabel: 'Lun' },
  { value: 2, label: 'Mardi', shortLabel: 'Mar' },
  { value: 3, label: 'Mercredi', shortLabel: 'Mer' },
  { value: 4, label: 'Jeudi', shortLabel: 'Jeu' },
  { value: 5, label: 'Vendredi', shortLabel: 'Ven' },
  { value: 6, label: 'Samedi', shortLabel: 'Sam' },
];

const DEFAULT_WORKING_HOURS = {
  morning_start: 8,
  morning_end: 12,
  afternoon_start: 14,
  afternoon_end: 18,
  work_days: [1, 2, 3, 4, 5]
};

export default function WorkingHoursPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workingHours, setWorkingHours] = useState(DEFAULT_WORKING_HOURS);
  const [originalHours, setOriginalHours] = useState(DEFAULT_WORKING_HOURS);

  useEffect(() => {
    fetchWorkingHours();
  }, []);

  const fetchWorkingHours = async () => {
    try {
      const response = await fetch(`${API}/working-hours`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setWorkingHours(data);
        setOriginalHours(data);
      }
    } catch (error) {
      console.error('Failed to fetch working hours:', error);
      toast.error('Erreur de chargement des heures de travail');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/working-hours`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(workingHours)
      });

      if (response.ok) {
        const data = await response.json();
        setWorkingHours(data);
        setOriginalHours(data);
        toast.success('Heures de travail enregistrées');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setWorkingHours(originalHours);
    toast.info('Modifications annulées');
  };

  const handleResetDefaults = () => {
    setWorkingHours(DEFAULT_WORKING_HOURS);
    toast.info('Valeurs par défaut restaurées');
  };

  const toggleDay = (dayValue) => {
    setWorkingHours(prev => {
      const newDays = prev.work_days.includes(dayValue)
        ? prev.work_days.filter(d => d !== dayValue)
        : [...prev.work_days, dayValue].sort((a, b) => a - b);
      return { ...prev, work_days: newDays };
    });
  };

  const hasChanges = JSON.stringify(workingHours) !== JSON.stringify(originalHours);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="working-hours-loading">
        <div className="flex justify-between items-center">
          <Skeleton className="h-10 w-64" />
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="working-hours-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight uppercase">
            Heures de Travail
          </h1>
          <p className="text-muted-foreground mt-1 font-mono text-sm uppercase tracking-wider">
            Configurez les plages horaires de travail pour la visualisation du planning
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleResetDefaults}
            className="rounded-sm border-2"
            data-testid="reset-defaults-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Valeurs par défaut
          </Button>
          {hasChanges && (
            <Button
              variant="outline"
              onClick={handleReset}
              className="rounded-sm border-2"
              data-testid="cancel-btn"
            >
              Annuler
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="btn-industrial rounded-sm"
            data-testid="save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Horaires */}
        <Card className="border-2 rounded-sm">
          <CardHeader className="border-b border-border">
            <CardTitle className="font-heading font-semibold text-lg uppercase tracking-tight flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Plages Horaires
            </CardTitle>
            <CardDescription className="font-mono text-xs uppercase tracking-wider">
              Définissez les heures d'ouverture de l'atelier
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Morning Hours */}
            <div className="space-y-4">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-tight text-primary">
                Matin
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="industrial-label">Début</Label>
                  <Select
                    value={workingHours.morning_start.toString()}
                    onValueChange={(value) => setWorkingHours({ ...workingHours, morning_start: parseInt(value) })}
                  >
                    <SelectTrigger className="rounded-sm border-2" data-testid="morning-start-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map((hour) => (
                        <SelectItem key={hour} value={hour.toString()}>
                          {hour.toString().padStart(2, '0')}:00
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="industrial-label">Fin</Label>
                  <Select
                    value={workingHours.morning_end.toString()}
                    onValueChange={(value) => setWorkingHours({ ...workingHours, morning_end: parseInt(value) })}
                  >
                    <SelectTrigger className="rounded-sm border-2" data-testid="morning-end-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map((hour) => (
                        <SelectItem key={hour} value={hour.toString()}>
                          {hour.toString().padStart(2, '0')}:00
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Afternoon Hours */}
            <div className="space-y-4">
              <h3 className="font-heading font-semibold text-sm uppercase tracking-tight text-primary">
                Après-midi
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="industrial-label">Début</Label>
                  <Select
                    value={workingHours.afternoon_start.toString()}
                    onValueChange={(value) => setWorkingHours({ ...workingHours, afternoon_start: parseInt(value) })}
                  >
                    <SelectTrigger className="rounded-sm border-2" data-testid="afternoon-start-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map((hour) => (
                        <SelectItem key={hour} value={hour.toString()}>
                          {hour.toString().padStart(2, '0')}:00
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="industrial-label">Fin</Label>
                  <Select
                    value={workingHours.afternoon_end.toString()}
                    onValueChange={(value) => setWorkingHours({ ...workingHours, afternoon_end: parseInt(value) })}
                  >
                    <SelectTrigger className="rounded-sm border-2" data-testid="afternoon-end-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map((hour) => (
                        <SelectItem key={hour} value={hour.toString()}>
                          {hour.toString().padStart(2, '0')}:00
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-secondary/50 rounded-sm p-4 mt-4">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">Horaires configurés :</span>{' '}
                {workingHours.morning_start.toString().padStart(2, '0')}:00 - {workingHours.morning_end.toString().padStart(2, '0')}:00
                {' et '}
                {workingHours.afternoon_start.toString().padStart(2, '0')}:00 - {workingHours.afternoon_end.toString().padStart(2, '0')}:00
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Working Days */}
        <Card className="border-2 rounded-sm">
          <CardHeader className="border-b border-border">
            <CardTitle className="font-heading font-semibold text-lg uppercase tracking-tight flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Jours Ouvrés
            </CardTitle>
            <CardDescription className="font-mono text-xs uppercase tracking-wider">
              Sélectionnez les jours de travail de la semaine
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {DAYS_OF_WEEK.map((day) => (
                <div
                  key={day.value}
                  className={`flex items-center justify-between p-3 rounded-sm border-2 transition-colors cursor-pointer ${
                    workingHours.work_days.includes(day.value)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-muted-foreground'
                  }`}
                  onClick={() => toggleDay(day.value)}
                  data-testid={`day-${day.value}`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={workingHours.work_days.includes(day.value)}
                      onCheckedChange={() => toggleDay(day.value)}
                      className="rounded-sm"
                    />
                    <span className="font-medium">{day.label}</span>
                  </div>
                  <span className={`font-mono text-xs uppercase tracking-wider ${
                    workingHours.work_days.includes(day.value)
                      ? 'text-primary'
                      : 'text-muted-foreground'
                  }`}>
                    {workingHours.work_days.includes(day.value) ? 'Ouvré' : 'Fermé'}
                  </span>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="bg-secondary/50 rounded-sm p-4 mt-6">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">
                  {workingHours.work_days.length} jour(s) ouvré(s)
                </span>
                {workingHours.work_days.length > 0 && (
                  <span>
                    {' : '}
                    {workingHours.work_days
                      .map(d => DAYS_OF_WEEK.find(day => day.value === d)?.shortLabel)
                      .join(', ')}
                  </span>
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview */}
      <Card className="border-2 rounded-sm">
        <CardHeader className="border-b border-border">
          <CardTitle className="font-heading font-semibold text-lg uppercase tracking-tight">
            Aperçu du Planning
          </CardTitle>
          <CardDescription className="font-mono text-xs uppercase tracking-wider">
            Visualisez les heures ouvrées sur le planning
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="flex gap-1 overflow-x-auto pb-2">
            {Array.from({ length: 24 }, (_, hour) => {
              const isMorning = hour >= workingHours.morning_start && hour < workingHours.morning_end;
              const isAfternoon = hour >= workingHours.afternoon_start && hour < workingHours.afternoon_end;
              const isWorking = isMorning || isAfternoon;

              return (
                <div
                  key={hour}
                  className={`flex-shrink-0 w-10 h-16 rounded-sm flex flex-col items-center justify-center transition-colors ${
                    isWorking
                      ? 'bg-green-100 dark:bg-green-950 border-2 border-green-500'
                      : 'bg-gray-100 dark:bg-gray-900 border border-border'
                  }`}
                >
                  <span className={`font-mono text-xs ${isWorking ? 'text-green-700 dark:text-green-400 font-medium' : 'text-muted-foreground'}`}>
                    {hour.toString().padStart(2, '0')}h
                  </span>
                </div>
              );
            })}
          </div>
          <div className="flex gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-sm bg-green-100 dark:bg-green-950 border-2 border-green-500" />
              <span className="text-sm text-muted-foreground">Heures ouvrées</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-sm bg-gray-100 dark:bg-gray-900 border border-border" />
              <span className="text-sm text-muted-foreground">Heures non ouvrées</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
